import React from "react";
import { CircleCheck, TriangleAlert } from "lucide-react";

import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";
import {
  GraphLibraryType,
  GraphLibraryTypeLabels,
  VersioningState,
  VersioningStateLabels,
} from "../../models/ClientConfiguration/VersioningState";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { dialogMedium } from "@/lib/dialogSize";

/**
 * Serialized by the server as `GraphLibraryVersioningAuditData`.
 *
 * PascalCase throughout, and enums arrive as the C# member's own name — `"Disabled"`, `"TemplateFiles"`.
 * This is not the casing the API uses for its own responses, so the enum-typed fields are held as raw
 * strings here and converted before anything compares or labels them.
 */
interface VersioningAuditData {
  LibraryType: string;
  ConfiguredDriveName: string | null;
  LibraryName: string | null;
  LibraryUrl: string | null;
  RequestedVersioningEnabled: boolean;
  WriteAccepted: boolean;
  FailureSummary: string | null;
}

/** Serialized by the server as `GraphLibraryVersioningAuditState`; same casing caveat as above. */
interface VersioningAuditState {
  VersioningState: string;
  MajorVersionLimit: number | null;
}

const parse = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Turns an enum member name from an audit payload into the value this app's enums carry.
 *
 * The app's enums hold the API's camelCase spelling of the same members, so lowercasing the first
 * letter maps one onto the other. Comparing the raw value instead silently never matches — every label
 * falls back to the raw string and every equality check reads false, which presents an applied change
 * as an unconfirmed one.
 */
const asEnumValue = <T extends string>(raw: string | null | undefined): T | null =>
  raw ? ((raw.charAt(0).toLowerCase() + raw.slice(1)) as T) : null;

const describeState = (state: VersioningAuditState | null): string => {
  if (!state) return "N/A";

  const versioningState = asEnumValue<VersioningState>(state.VersioningState);
  const label =
    (versioningState && VersioningStateLabels[versioningState]) ?? state.VersioningState;

  // A cap only describes a library whose versioning is on, and the server stores null for "unlimited".
  if (versioningState !== VersioningState.Enabled) return label;

  return state.MajorVersionLimit != null
    ? `${label} · keeps up to ${state.MajorVersionLimit} versions`
    : `${label} · keeps unlimited versions`;
};

/**
 * A versioning change made from the portal.
 *
 * The three outcomes are kept apart on purpose. A refusal changed nothing, so its before and after
 * states are identical and only the requested direction says what was attempted. An accepted change
 * whose resulting state is indeterminate went in and could not be confirmed — presenting that as done
 * would assert a setting nothing established.
 */
const ViewGraphLibraryVersioningChangeAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
}> = ({ auditLog, open, onClose }) => {
  const details = parse<VersioningAuditData>(auditLog?.additionalData ?? null);
  const before = parse<VersioningAuditState>(auditLog?.oldValue ?? null);
  const after = parse<VersioningAuditState>(auditLog?.newValue ?? null);

  const accepted = details?.WriteAccepted === true;
  const confirmed =
    accepted &&
    asEnumValue<VersioningState>(after?.VersioningState) ===
      (details?.RequestedVersioningEnabled ? VersioningState.Enabled : VersioningState.Disabled);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={`${dialogMedium} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex flex-col gap-3 overflow-y-auto max-h-[60vh] pr-1">
              <div className="flex items-center gap-2">
                {accepted ? (
                  <Badge variant={confirmed ? "success" : "warning"}>
                    {confirmed ? <CircleCheck /> : <TriangleAlert />}
                    {confirmed ? "Applied" : "Accepted, not confirmed"}
                  </Badge>
                ) : (
                  <Badge variant="danger">
                    <TriangleAlert />
                    Refused
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  versioning {details?.RequestedVersioningEnabled ? "on" : "off"} was requested
                </span>
              </div>

              {details && (
                <>
                  <Separator />
                  <LabelValuePair
                    label="Target"
                    value={
                      GraphLibraryTypeLabels[
                        asEnumValue<GraphLibraryType>(details.LibraryType) as GraphLibraryType
                      ] ?? details.LibraryType
                    }
                  />
                  <LabelValuePair label="Library" value={details.LibraryName ?? "N/A"} />
                  {/* Named separately: where the two differ, the configuration and the site have drifted. */}
                  {details.ConfiguredDriveName &&
                    details.ConfiguredDriveName !== details.LibraryName && (
                      <LabelValuePair
                        label="Configured Name"
                        value={details.ConfiguredDriveName}
                      />
                    )}
                  <LabelValuePair label="Library URL" value={details.LibraryUrl ?? "N/A"} />
                </>
              )}

              <Separator />
              <LabelValuePair label="Versioning Before" value={describeState(before)} />
              <LabelValuePair label="Versioning After" value={describeState(after)} />

              {!accepted && (
                <span className="text-xs text-muted-foreground">
                  Nothing changed in SharePoint, so the state on record is the one that was already
                  there.
                </span>
              )}

              {details?.FailureSummary && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-bold">Reported Problem:</span>
                    <span className="text-sm whitespace-pre-wrap break-words">
                      {details.FailureSummary}
                    </span>
                  </div>
                </>
              )}

              <Separator />
              <LabelValuePair label="Performed By" value={auditLog.performerEmailAddress} />
              <LabelValuePair label="Event Date" value={auditLog.eventDate} />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewGraphLibraryVersioningChangeAuditLogDetailsModal;
